import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { filterNil } from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { finalize, map, shareReplay, switchMap } from 'rxjs/operators';

import { UiStatisticsCommandHandler } from 'application/ui-statistics/ui-statistics-command.handler';
import { FirstCoachingPlanModuleDialogData } from 'dialog/first-coaching-plan-module-dialog/first-coaching-plan-module-dialog.data';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { AppService } from 'presentation/app.service';

@UntilDestroy()
@Component({
	selector: 'spr-first-coaching-plan-module-dialog',
	templateUrl: './first-coaching-plan-module-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: 'flex flex-col space-y-6',
	},
})
export class FirstCoachingPlanModuleDialogComponent implements OnInit {
	public i18n = 'presentation.common.dialog.first-coaching-plan-module-dialog';

	public moduleDescription$: Observable<string>;
	private _data: FirstCoachingPlanModuleDialogData;

	constructor(
		private _router: Router,
		private _appService: AppService,
		private _config: DynamicDialogConfig,
		private _dialogRef: DynamicDialogRef,
		moduleApplicationPort: ModuleApplicationPort,
		private _uiStatisticsCommandHandler: UiStatisticsCommandHandler
	) {
		this._data = this._config.data as FirstCoachingPlanModuleDialogData;

		this.moduleDescription$ = moduleApplicationPort
			.observeModuleById(this._data.coachingPlanModuleId)
			.pipe(
				map((data) => data.data),
				filterNil(),
				switchMap((module) => module.localization$),
				filterNil(),
				map((loc) => loc.description),
				shareReplay({ refCount: true, bufferSize: 1 })
			);
	}

	public ngOnInit() {
		this._uiStatisticsCommandHandler
			.createCoacheeUiEvent({
				moduleId: this._data.coachingPlanModuleId,
				context: 'FirstCoachingPlanModuleDialog',
				userId: this._data.userId,
				event: 'Opened',
			})
			.pipe(untilDestroyed(this))
			.subscribe();
	}

	public onStartClick() {
		this._uiStatisticsCommandHandler
			.createCoacheeUiEvent({
				moduleId: this._data.coachingPlanModuleId,
				context: 'FirstCoachingPlanModuleDialog',
				userId: this._data.userId,
				event: 'StartClicked',
			})
			.pipe(
				switchMap(() =>
					this._router.navigate([
						'/activity',
						this._data.coachingPlanModuleId,
						'user',
						this._data.userId,
					])
				),
				finalize(() => {
					this._dialogRef.close();
					this._appService.markFirstModuleDialogAsClosed();
				}),
				untilDestroyed(this)
			)
			.subscribe();
	}
}
